import { PERMISSIONS } from "@/constants/permissions"
import { useRecordMemoryShown } from "@/features/material-compliance/api/memoryApi"
import { invalidateAnswerWrites } from "@/features/material-compliance/api/questionnaireProcessingApi"
import { MemoryBecause } from "@/features/material-compliance/components/memory/MemoryBecause"
import { MemoryRating } from "@/features/material-compliance/components/memory/MemoryRating"
import { MemoryTrail } from "@/features/material-compliance/components/memory/MemoryTrail"
import { useConflictResolution } from "@/features/material-compliance/components/memory/useConflictResolution"
import { useTranslation } from "@/hooks/useTranslation/useTranslation"
import type {
  MemoryAnswerSource,
  MemoryAnswerSuggestion as MemoryAnswerSuggestionModel,
} from "@/types/questionnaire/models"
import { useQueryClient } from "@tanstack/react-query"
import { AlertTriangle, Check, Sparkles } from "lucide-react"
import { useEffect, useRef } from "react"

export interface AnswerMemorySuggestionProps {
  /** The response pair this suggestion sits on — the thing a sighting is ABOUT. */
  responsePairId: string
  /** The questionnaire this response pair belongs to — scopes the post-resolve refresh to
   * this document's own reads (`invalidateAnswerWrites`) instead of every active query. */
  questionnaireId: string
  suggestion: MemoryAnswerSuggestionModel
}

function Sources({ citations }: { citations?: MemoryAnswerSource[] | null }) {
  // THE EVIDENCE TRAVELS WITH THE ANSWER. Offering a compliance answer without what backed
  // it is the failure mode this whole feature has to avoid.
  if (!citations?.length) return null
  return (
    <p className="text-[11px] text-grey5">
      {citations.map((c) => c.label).join(", ")}
    </p>
  )
}

/**
 * An answer this org already stood behind, offered beside a question that means the same
 * thing (Turnus Memory #3).
 *
 * PURELY ADVISORY. This component has no path that writes an answer — not on render, not on
 * click, not at all. A reviewer reads it and types whatever they decide. That is a
 * deliberate narrowing from the earlier "Use this" button, which wrote the answer directly:
 * on a compliance questionnaire the value of memory is that it REMINDS, and the moment it
 * also fills the box it becomes something a reviewer has to defend against rather than read.
 *
 * RATING IS SEPARATE FROM USING. The thumbs are about the memory, not about this answer box
 * — see `MemoryRating`. A thumbs-down is still the revert that retires a wrong entry after
 * three, which is the only way a bad correction stops haunting the org (scenario #7).
 *
 * TWO ANSWERS THAT DISAGREE ARE SHOWN AS TWO. When `conflict` is non-empty there is no
 * single remembered answer and the parent's `entryId`/`answer`/`because` are all null — so
 * the conflict branch is checked first, before anything reads them. Each side is rated
 * independently, and the component never marks one as the right one.
 *
 * WHY IT SAYS WHY. The line under each answer is not decoration. An answer a compliance
 * reviewer cannot trace is either trusted blindly — which is how an indefensible answer
 * reaches a customer — or it discredits the feature. The collapsible trail underneath
 * carries the rest: what kind of claim it was, from which document, when, and by whom.
 *
 * A PERSON CAN STILL SETTLE A CONFLICT, which is different from the system picking one.
 * Clicking a side highlights it; confirming calls `resolve-conflict`, which retires every
 * other side server side. Once that lands, the confirmed answer renders through the same
 * branch an uncontested memory does — the contradiction is gone, not merely hidden.
 */
export function AnswerMemorySuggestion({
  responsePairId,
  questionnaireId,
  suggestion,
}: AnswerMemorySuggestionProps) {
  const { translate } = useTranslation()
  const recordShown = useRecordMemoryShown(PERMISSIONS.QUEST_RUN)
  const queryClient = useQueryClient()
  const conflictResolution = useConflictResolution({
    surface: suggestion.surface,
    targetType: "response",
    targetId: responsePairId,
    permission: PERMISSIONS.QUEST_RUN,
    invalidate: () => invalidateAnswerWrites(queryClient, questionnaireId),
  })

  const { entryId, surface, answer, conflict } = suggestion
  const allSides = conflict ?? []
  const sides = allSides.filter(
    (s) => !conflictResolution.retiredEntryIds.includes(s.entryId),
  )
  // A FOREIGN SIDE MEANS A NEW CONFLICT, and the latch from the last one must not survive
  // it — see `reset`'s own note in the hook. Checked and applied during render (not an
  // effect): by the time anything below reads `resolvedWinnerId`, it must already reflect
  // this conflict, not the previous one.
  if (
    conflictResolution.resolvedWinnerId &&
    allSides.some(
      (s) =>
        s.entryId !== conflictResolution.resolvedWinnerId &&
        !conflictResolution.retiredEntryIds.includes(s.entryId),
    )
  ) {
    conflictResolution.reset()
  }

  const winner = conflictResolution.resolvedWinnerId
    ? allSides.find((s) => s.entryId === conflictResolution.resolvedWinnerId)
    : undefined
  // `!winner` FIRST — after a resolve `sides` legitimately drops to one and `winner` is
  // truthy, short-circuiting before the length check. `> 1` not `> 0`: a flagged conflict can
  // still arrive with one side lost upstream; rendering that lone survivor here would be the
  // UI picking a winner the server explicitly refused to pick.
  const isConflict = !winner && sides.length > 1

  // What is actually on screen. The reporting effect and the render must agree: hooks still
  // run when a component returns null, so a condition checked only at render would record
  // sightings for a box nobody saw and inflate the denominator of the one number this
  // feature is judged on.
  const renderable = isConflict || winner || (!!entryId && !!answer)

  // EVERY SIDE OF A CONFLICT IS ITS OWN SIGHTING. Both were shown, and the accept rate of
  // each is only meaningful against the number of times it was actually put in front of
  // somebody. Reported once per (question, entry) — the same answer offered against a
  // second question is a second sighting, so deduping on the entry alone would drop it.
  const shownIds = isConflict
    ? sides.map((s) => s.entryId)
    : entryId
      ? [entryId]
      : []
  const sighting = shownIds.length
    ? `${responsePairId}:${shownIds.join(",")}`
    : null

  // Reported once per ENTRY ID, not once per sighting STRING — a resolve changes this same
  // response pair's composite key shape (`pair:a,b` while conflicted becomes `pair:a` once
  // the resolution's refetch lands and the winner renders through the ordinary branch), and
  // comparing only the latest key would read that as a new sighting and report the winner
  // shown twice for one continuous look. Tracked per response pair so a genuinely different
  // one still starts its own set.
  const reportedIds = useRef<{ pair: string; ids: Set<string> }>({
    pair: responsePairId,
    ids: new Set(),
  })
  useEffect(() => {
    if (!renderable || shownIds.length === 0) return
    if (reportedIds.current.pair !== responsePairId) {
      reportedIds.current = { pair: responsePairId, ids: new Set() }
    }
    const unseen = shownIds.filter((id) => !reportedIds.current.ids.has(id))
    if (unseen.length === 0) return
    unseen.forEach((id) => reportedIds.current.ids.add(id))
    recordShown.mutate(
      unseen.map((id) => ({
        entryId: id,
        surface,
        targetType: "response",
        targetId: responsePairId,
      })),
    )
    // recordShown is a stable mutation handle; re-running on it would re-report.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [renderable, responsePairId, sighting, surface])

  if (!renderable) return null

  if (isConflict) {
    return (
      <div
        className="flex flex-col gap-2 rounded-md border border-attention bg-grey2 px-3 py-2 text-left"
        data-testid={`memory-answer-conflict-${responsePairId}`}
      >
        <div className="flex items-start gap-2">
          <AlertTriangle
            size={13}
            className="mt-[2px] shrink-0 text-attention"
            aria-hidden="true"
            data-testid="memory-conflict-icon"
          />
          <div className="flex flex-col gap-[2px]">
            <p className="text-[11px] tracking-wide text-attention uppercase">
              {translate("MC_MEMORY_CONFLICT_TITLE")}
            </p>
            <p className="text-[11px] text-grey5">
              {translate("MC_MEMORY_CONFLICT_HINT")}
            </p>
          </div>
        </div>
        {/* NO SIDE IS MARKED, ordered or styled as the recommendation. The server ranked
            them for a stable display order, not as advice — but a person can still select
            one to highlight it, then confirm it, which is a decision rather than a guess.
            SELECTION IS ITS OWN BUTTON, not a click handler on the whole card: the card
            already hosts the rating thumbs, the trail toggle and (once selected) the
            confirm button, and a click anywhere in that tree bubbles. A card-level
            onClick would re-select the row every time one of those is used instead of
            doing what it was clicked for — and a card-level onKeyDown catches the SAME
            Enter/Space the nested buttons need, so keyboard users could never reach
            Confirm at all. */}
        {sides.map((side) => {
          const selected = conflictResolution.selectedEntryId === side.entryId
          return (
            <div
              key={side.entryId}
              className={`flex flex-col gap-1.5 rounded border px-2 py-1.5 text-left transition-colors ${
                selected ? "border-attention bg-grey3" : "border-grey4"
              }`}
              data-testid={`memory-conflict-side-${side.entryId}`}
            >
              <div className="flex items-start justify-between gap-2">
                <div className="flex min-w-0 items-start gap-2">
                  <button
                    type="button"
                    aria-pressed={selected}
                    aria-label={`${translate("MC_MEMORY_CONFLICT_SELECT")}: ${side.answer}`}
                    onClick={() => conflictResolution.select(side.entryId)}
                    className={`mt-[4px] size-[11px] shrink-0 rounded-full border transition-colors ${
                      selected
                        ? "border-attention bg-attention"
                        : "border-grey5 hover:border-white"
                    }`}
                    data-testid={`memory-conflict-select-${side.entryId}`}
                  />
                  <div className="flex min-w-0 flex-col gap-[2px]">
                    <p className="text-[13px] font-semibold whitespace-pre-wrap text-white">
                      {side.answer}
                    </p>
                    <MemoryBecause
                      origin={side.origin}
                      surface="answer"
                      fallback={side.because}
                      testId={side.entryId}
                    />
                    <Sources citations={side.citations} />
                    <MemoryTrail
                      origin={side.origin}
                      testId={side.entryId}
                      entryId={side.entryId}
                      surface={surface}
                    />
                  </div>
                </div>
                <MemoryRating
                  target={{
                    entryId: side.entryId,
                    surface,
                    targetType: "response",
                    targetId: responsePairId,
                  }}
                  permission={PERMISSIONS.QUEST_RUN}
                  testId={side.entryId}
                  subjectLabel={side.answer ?? undefined}
                />
              </div>
              {selected ? (
                <button
                  type="button"
                  disabled={conflictResolution.isConfirming}
                  onClick={() => conflictResolution.confirm(side.entryId)}
                  className="flex items-center justify-center gap-1 self-start rounded bg-attention px-2 py-0.5 text-[10px] font-semibold text-black transition-opacity disabled:opacity-60"
                  data-testid={`memory-confirm-correct-${side.entryId}`}
                >
                  <Check size={11} aria-hidden="true" />
                  {translate("MC_MEMORY_CONFIRM_CORRECT")}
                </button>
              ) : null}
            </div>
          )
        })}
      </div>
    )
  }

  const winnerEntryId = winner ? winner.entryId : entryId
  const winnerAnswer = winner ? winner.answer : answer
  const winnerOrigin = winner ? winner.origin : suggestion.origin
  const winnerBecause = winner ? winner.because : suggestion.because
  const winnerCitations = winner ? winner.citations : suggestion.citations

  return (
    <div
      className="flex flex-col gap-2 rounded-md border border-grey4 bg-grey2 px-3 py-2 text-left"
      data-testid={`memory-answer-${responsePairId}`}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="flex min-w-0 items-start gap-2">
          <Sparkles
            size={13}
            className="mt-[2px] shrink-0 text-attention"
            aria-hidden="true"
            data-testid="memory-icon"
          />
          <div className="flex flex-col gap-[2px]">
            <p className="text-[11px] tracking-wide text-grey5 uppercase">
              {translate("MC_MEMORY_SUGGESTS")}
            </p>
            <p className="text-[13px] font-semibold whitespace-pre-wrap text-white">
              {winnerAnswer}
            </p>
            <MemoryBecause
              origin={winnerOrigin}
              surface="answer"
              fallback={winnerBecause}
              testId={responsePairId}
            />
            <Sources citations={winnerCitations} />
            {/* NAMED, NOT LINKED — still true, and still not a decision made here.
                `renderMemorySource` (used by both MemoryBecause and MemoryTrail) only
                links a `declaration` source; a questionnaire's own route depends on its
                upload type (PDF vs. Excel), which `origin` has no way to know, so this
                surface's source stays a name rather than risking a dead link. */}
            <MemoryTrail
              origin={winnerOrigin}
              testId={responsePairId}
              entryId={winnerEntryId}
              surface={surface}
            />
          </div>
        </div>
        {winnerEntryId ? (
          <MemoryRating
            target={{
              entryId: winnerEntryId,
              surface,
              targetType: "response",
              targetId: responsePairId,
            }}
            permission={PERMISSIONS.QUEST_RUN}
            testId={responsePairId}
          />
        ) : null}
      </div>
    </div>
  )
}
